import { useCallback, useState } from "react";
import { useLocation } from "wouter";
import { omit } from "lodash";
import { notify } from "../../../../components/Notification";
import {
  DeletionImpact,
  useCreateTransactionMutation,
  useDeleteSavingMutation,
  useDeleteTransactionMutation,
  useLazyGetAccountDeletionImpactQuery,
  useUpdateSavingAccountStatusMutation,
  useUpdateTransactionMutation,
} from "../../../../services";
import { getErrorMessages } from "../../../../services/errors";
import { formatDate } from "../../../../utils";
import { CreateTransactionsObject } from "../../../Transactions";
import { ProgressObject } from "../../panels";
import { UseSavingActionsProps, UseSavingActionsReturn } from "./types";

export const useSavingActions = ({
  id,
  saving,
  refetchSaving,
  openDeleteModal,
  openProgressDeleteModal,
  openActivateModal,
  closeDeleteModal,
  closeProgressDeleteModal,
  closeActivateModal,
  progressToDelete,
  setProgressToDelete,
  closePanel,
}: UseSavingActionsProps): UseSavingActionsReturn => {
  const [, setLocation] = useLocation();
  const [isLoadingImpact, setIsLoadingImpact] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deletionImpact, setDeletionImpact] = useState<DeletionImpact | null>(
    null,
  );
  const [deleteSaving] = useDeleteSavingMutation();
  const [updateSavingAccountStatus] = useUpdateSavingAccountStatusMutation();
  const [fetchImpact] = useLazyGetAccountDeletionImpactQuery();
  const [createTransaction] = useCreateTransactionMutation();
  const [updateTransaction] = useUpdateTransactionMutation();
  const [deleteTransaction] = useDeleteTransactionMutation();

  const fetchDeletionImpact = useCallback(async () => {
    setIsLoadingImpact(true);
    try {
      const impact = await fetchImpact(saving.accountId).unwrap();
      setDeletionImpact(impact);
    } catch {
      // If we can't fetch impact, allow deletion with fallback to static warning.
      // The global middleware shows the toast.
      setDeletionImpact(null);
    } finally {
      setIsLoadingImpact(false);
    }
  }, [fetchImpact, saving.accountId]);

  const handleEdit = useCallback(() => {
    // Edit modal is managed in parent component
  }, []);

  const handleDelete = useCallback(() => {
    setDeleteError(null);
    openDeleteModal();
  }, [openDeleteModal]);

  const handleOnStatusChangeClick = useCallback(async () => {
    try {
      const message = saving.isActive
        ? "Saving deactivated"
        : "Saving reactivated";
      await updateSavingAccountStatus({
        id,
        isActive: !saving.isActive,
      }).unwrap();
      notify.success(message);
      closeActivateModal();
    } catch (err: any) {
      notify.apiError(err);
      closeActivateModal();
    }
  }, [id, saving.isActive, updateSavingAccountStatus, closeActivateModal]);

  const handleDeleteProgress = useCallback(async () => {
    if (!progressToDelete) {
      return;
    }
    try {
      await deleteTransaction(progressToDelete.id).unwrap();
      notify.success("Saving Progress deleted");
      setProgressToDelete(null);
      closeProgressDeleteModal();
      // The transaction ripple does not carry `{ Saving, id }`, so the detail
      // query still needs an explicit refetch.
      await refetchSaving();
    } catch {
      // Global middleware shows the toast for mutation failures.
      setProgressToDelete(null);
      closeProgressDeleteModal();
    }
  }, [
    progressToDelete,
    deleteTransaction,
    setProgressToDelete,
    closeProgressDeleteModal,
    refetchSaving,
  ]);

  const handleOnDeleteClick = useCallback(async () => {
    setDeleteError(null);
    setIsDeleting(true);
    try {
      await deleteSaving(id).unwrap();
      notify.success("Saving Deleted");
      setLocation("/savings");
      closeDeleteModal();
    } catch (err: any) {
      // Keep the modal open with an inline error so the user can retry.
      setDeleteError(getErrorMessages(err).join(" "));
    } finally {
      setIsDeleting(false);
    }
  }, [id, deleteSaving, setLocation, closeDeleteModal]);

  const handleSubmitProgress = useCallback(
    async (data: ProgressObject, action: "create" | "edit" | "duplicate") => {
      try {
        const payload: CreateTransactionsObject = {
          ...omit(data, ["id"]),
          transactionDate: formatDate(data.transactionDate),
          isRecurring: false,
          frequency: null,
        };

        if (action === "edit") {
          await updateTransaction({
            transactionId: data.id!,
            transaction: payload,
          }).unwrap();
          notify.success("Transaction edited successfully!");
        } else if (action === "duplicate") {
          await createTransaction(payload).unwrap();
          notify.success("Transaction duplicated successfully!");
        } else {
          await createTransaction(payload).unwrap();
          notify.success("Transaction created successfully!");
        }

        closePanel();
        // The transaction ripple does not carry `{ Saving, id }`, so the detail
        // query still needs an explicit refetch.
        await refetchSaving();
      } catch {
        // Global middleware shows the toast for mutation failures.
      }
    },
    [createTransaction, updateTransaction, closePanel, refetchSaving],
  );

  return {
    handleEdit,
    handleDelete,
    handleOnStatusChangeClick,
    handleDeleteProgress,
    handleOnDeleteClick,
    handleSubmitProgress,
    isLoadingImpact,
    isDeleting,
    deleteError,
    deletionImpact,
    fetchDeletionImpact,
  };
};
